import { SxProps } from '@mui/material/styles';
import {
	Controller,
	type ControllerProps,
	type FieldPath,
	type FieldValues
} from 'react-hook-form';

import { HcoSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcoFieldNames';
import { HcpSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcpFieldNames';

import { useDcrContext } from '../_context/DcrContext';
import { FilteredField } from '../FilteredField/FilteredField';

type FilterDisabled = {
	filterable: false;
	section?: never;
};

type FilterEnabled = {
	filterable?: true;
	section: HcoSection | HcpSection;
};

type LocalProps = (FilterEnabled | FilterDisabled) & {
	customSx?: SxProps;
};

type Props<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = ControllerProps<TFieldValues, TName> & LocalProps;

export const DcrFormController = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	filterable = true,
	section,
	customSx,
	...props
}: Props<TFieldValues, TName>) => {
	const { unavailableFields } = useDcrContext();

	// Note(Filip): This is needed because of the nested entities (e.g. address.0.street)
	const parts = props.name.split('.');
	const rootName = parts[0];
	const leafName = parts[parts.length - 1];

	const isUnavailable =
		unavailableFields?.includes(rootName) ||
		unavailableFields?.includes(leafName);

	if (isUnavailable) {
		return null;
	}

	return (
		<FilteredField
			name={props.name}
			section={section ?? 'name'}
			enabled={filterable}
			customSx={customSx}
		>
			<Controller {...props} />
		</FilteredField>
	);
};
